import { heroFromBuild } from '../../../content/characters/character-options.js';
import type { CharacterBuild } from '../../../shared/game/character-build.js';
import type { HeroDefinition, HeroId } from '../../../shared/game/world-types.js';
import type { ServerMessage } from '../../../shared/network/protocol.js';
import type { CharacterStore } from '../../characters/character-store.js';
import { PartyStage } from '../../rendering/entities/party-stage.js';
import { element as el } from '../dom.js';
import { icon } from '../icons.js';
import { setTooltip } from '../tooltips.js';
import { playSlab } from '../../audio/slab-sound.js';
import { gearIcon } from '../characters/gear-icons.js';
import { classIcon } from '../content-icons.js';
import { portraitFrame } from '../portraits.js';

export type PartyChoice = { heroIds: HeroId[] } | { characters: CharacterBuild[] };
type Options = Extract<ServerMessage, { type: 'party-options' }>;
interface Entry { hero: HeroDefinition; build: CharacterBuild | null }

/**
 * A new party is chosen from the account's saved characters, up to the seats the server offered this player.
 * A reserved party (reconnecting) shows the heroes already in the room and can only resume.
 */
export class PartyPicker {
  private choices: HTMLInputElement[] = [];
  private entries: Entry[] = [];
  private busy = false;
  private stage: PartyStage | null = null;
  private options: Options | null = null;
  private loading = false;
  constructor(private store: CharacterStore, private hooks: { enter: (choice: PartyChoice) => boolean; create: () => void; edit: (build: CharacterBuild) => void }) {
    el('party-form').addEventListener('submit', event => {
      event.preventDefault();
      const chosen = this.entries.filter((_, index) => this.choices[index]?.checked);
      if (this.busy || !chosen.length) return;
      const choice: PartyChoice = this.locked ? { heroIds: chosen.map(entry => entry.hero.id) } : { characters: chosen.map(entry => entry.build!) };
      if (this.hooks.enter(choice)) this.setBusy(true);
    });
    store.onChange(() => { if (this.options && !this.locked) this.render(); });
  }
  private get locked() { return !!this.options?.selectedHeroIds.length; }
  private get seats() { return this.options?.heroIds.length ?? 0; }

  show(options: Options) {
    this.options = options; this.busy = false; this.entries = []; this.choices = []; this.loading = !this.locked;
    el('party-room').textContent = `Room ${options.room} · ${options.content.map.name}`;
    this.render();
  }

  private render() {
    const options = this.options!;
    if (this.locked) {
      this.entries = options.selectedHeroIds.map(id => ({ hero: options.content.heroes.find(hero => hero.id === id)!, build: null }));
      this.draw(); return;
    }
    // Previews stand where the player's first seat is; the server builds the real heroes on entry.
    const seat = options.content.heroes.find(hero => hero.id === options.heroIds[0])!;
    void this.store.list().then(characters => {
      if (this.options !== options || this.locked) return;
      this.entries = characters.map(build => ({ hero: heroFromBuild(build, { id: build.id, spawn: seat.spawn, playerSlot: seat.playerSlot }), build }));
    }, error => { el('lobby-error').textContent = (error as Error).message; }).finally(() => { if (this.options === options) { this.loading = false; this.draw(); } });
    this.draw();
  }

  private draw() {
    const options = this.options!, locked = this.locked, list = el('party-roster');
    const previous = new Set(this.choices.filter(input => input.checked).map(input => input.value));
    list.replaceChildren(); this.choices = [];
    el('party-hint').textContent = locked
      ? 'Your party is reserved in this room. Rejoin with the same characters and continue where you left off.'
      : this.entries.length
        ? `Bring up to ${this.seats} of your characters. Only those you choose enter the world; you can play solo.`
        : this.loading ? 'Loading your characters…' : 'You have no characters yet. Create one to enter the world.';
    list.style.setProperty('--count', String(Math.max(1, this.entries.length)));
    el('party-stage').style.setProperty('--count', String(Math.max(1, this.entries.length)));
    for (const { hero, build } of this.entries) {
      const card = document.createElement('label'); card.className = 'party-choice tablet';
      card.style.setProperty('--hero-color', '#' + hero.color.toString(16).padStart(6, '0')); card.style.setProperty('--accent', '#' + hero.color.toString(16).padStart(6, '0'));
      const input = document.createElement('input'); input.type = 'checkbox'; input.value = hero.id;
      input.checked = locked || previous.has(hero.id); input.disabled = locked;
      input.setAttribute('aria-label', `Bring ${hero.name}`);
      const gem = document.createElement('span'); gem.className = 'tablet-gem'; gem.setAttribute('aria-hidden', 'true');
      const emblem = portraitFrame(hero.portrait, classIcon(hero.classId), 'tablet-portrait');
      const name = document.createElement('strong'); name.className = 'tablet-name'; name.textContent = hero.name;
      const cls = options.content.classes?.find(candidate => candidate.id === hero.classId);
      const role = document.createElement('span'); role.className = 'tablet-role'; role.textContent = `${cls?.name ?? hero.classId} · Level ${hero.level}`;
      const vitals = document.createElement('span'); vitals.className = 'tablet-vitals';
      const hp = document.createElement('span'); hp.className = 'tablet-vital'; hp.tabIndex = 0; setTooltip(hp, 'Hit points', `${hero.profile.maxHp} hit points`);
      hp.append(icon('health'), Object.assign(document.createElement('strong'), { textContent: String(hero.profile.maxHp) })); vitals.append(hp);
      for (const attack of hero.profile.attacks) {
        const weapon = document.createElement('span'); weapon.className = 'tablet-weapon'; weapon.tabIndex = 0; weapon.append(icon(gearIcon(attack.name))); setTooltip(weapon, attack.name, attack.name); vitals.append(weapon);
      }
      card.append(input, gem, emblem, name, role, vitals);
      if (build) {
        const tools = document.createElement('span'); tools.className = 'tablet-tools';
        const edit = document.createElement('button'); edit.type = 'button'; edit.className = 'tablet-tool'; edit.append(icon('pencil'));
        edit.addEventListener('click', event => { event.preventDefault(); this.hooks.edit(build); });
        const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'tablet-tool'; remove.append(icon('trash'));
        remove.addEventListener('click', event => {
          event.preventDefault();
          if (!confirm(`Delete ${build.name}? This cannot be undone.`)) return;
          void this.store.remove(build.id).catch(error => { el('lobby-error').textContent = (error as Error).message; });
        });
        setTooltip(edit, `Edit ${build.name}`, `Edit ${build.name}`); setTooltip(remove, `Delete ${build.name}`, `Delete ${build.name}`);
        tools.append(edit, remove); card.append(tools);
      }
      list.append(card);
      input.addEventListener('change', () => { this.refresh(input); playSlab(); }); this.choices.push(input);
    }
    if (!locked) {
      const create = document.createElement('button'); create.type = 'button'; create.className = 'party-choice party-new tablet';
      const plus = document.createElement('span'); plus.className = 'tablet-emblem'; plus.append(icon('party'));
      create.append(plus, Object.assign(document.createElement('strong'), { className: 'tablet-name', textContent: 'Create new character' }));
      create.addEventListener('click', () => this.hooks.create());
      list.append(create);
    }
    this.stage ??= new PartyStage(el('party-stage'));
    this.stage.show(this.entries.map(entry => entry.hero), new Set(this.choices.filter(input => input.checked).map(input => input.value)));
    el('party-stage').hidden = !this.entries.length;
    this.refresh();
    if (locked) el<HTMLButtonElement>('enter-game').focus();
  }
  setBusy(busy: boolean) { this.busy = busy; this.refresh(); }
  /** Keeps the choice within the seats: picking one more than allowed drops the oldest other pick. */
  private refresh(changed?: HTMLInputElement) {
    if (changed?.checked) {
      const picked = this.choices.filter(input => input.checked);
      if (picked.length > this.seats) picked.find(input => input !== changed)!.checked = false;
    }
    const count = this.choices.filter(input => input.checked).length;
    el('party-count').textContent = `${count} of ${this.seats} character${this.seats === 1 ? '' : 's'} selected`;
    const button = el<HTMLButtonElement>('enter-game'); button.disabled = this.busy || !count;
    button.textContent = this.busy ? 'Entering…' : `Enter with ${count || 'your'} character${count === 1 ? '' : 's'}`;
    this.choices.forEach((input, index) => { input.closest('label')!.classList.toggle('chosen', input.checked); this.stage?.setChosen(index, input.checked); });
  }
}
